import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Container, Typography, Card, Stack, Grid, 
  Button, TextField, Chip, Paper,
  Backdrop, CircularProgress, Alert, Snackbar, IconButton,
  InputAdornment, Avatar, Tooltip, Fade,
  LinearProgress, alpha, useTheme, Divider,
  Badge, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, SelectChangeEvent
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Hotel as HotelIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Build as BuildIcon,
  BusinessCenter as BusinessCenterIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DeleteSweep as DeleteSweepIcon,
  Dashboard as DashboardIcon,
  KingBed as KingBedIcon,
  AcUnit as AcUnitIcon,
  Wifi as WifiIcon,
  LocalBar as LocalBarIcon,
  Bathtub as BathtubIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  DateRange as DateRangeIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types based on API response
interface Room {
  id: number;
  product_id: string;
  name: string;
  price: string;
  stock_value: string;
  category: string;
  type: string;
  qty: number;
  instocks: number;
  totalQtyPosted: number;
  totalQtySold: number;
  is_assigned?: boolean;
  assigned_housekeeper_name?: string | null;
  assignment_status?: string | null;
  housekeeperId?: string | null;
  category_name?: string | null;
  last_updated: string;
  item_status?: 'dirty' | 'clean' | 'management' | 'out_of_order' | 'maintenance' | 'assigned' | 'available';
  room_status?: string;
}

// Assignment History Types
interface AssignmentHistory {
  id: number;
  assignmentDate: string;
  roomId: string;
  housekeeperId: string;
  supervisorId: string;
  status: 'assigned' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  business_id: string;
  branch_id: string;
  room_name?: string;
  housekeeper_name?: string;
  supervisor_name?: string;
}

interface HousekeeperInfo {
  user_id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Status options with vibrant colors
const STATUS_OPTIONS = [
  { 
    value: 'all', 
    label: 'All Rooms', 
    color: '#3b82f6', 
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    lightColor: '#dbeafe',
    icon: <DashboardIcon />,
    count: 0
  },
  { 
    value: 'clean', 
    label: 'Clean', 
    color: '#10b981', 
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    lightColor: '#d1fae5',
    icon: <CheckCircleOutlineIcon />,
    count: 0
  },
  { 
    value: 'dirty', 
    label: 'Dirty', 
    color: '#f59e0b', 
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    lightColor: '#fef3c7',
    icon: <DeleteSweepIcon />,
    count: 0
  },
  { 
    value: 'out_of_order', 
    label: 'Out of Order', 
    color: '#ef4444', 
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    lightColor: '#fee2e2',
    icon: <BlockIcon />,
    count: 0
  },
  { 
    value: 'maintenance', 
    label: 'Maintenance', 
    color: '#f97316', 
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    lightColor: '#ffedd5',
    icon: <BuildIcon />,
    count: 0
  },
  { 
    value: 'management', 
    label: 'Management', 
    color: '#8b5cf6', 
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    lightColor: '#ede9fe',
    icon: <BusinessCenterIcon />,
    count: 0
  }
];

// Room amenities icons
const ROOM_AMENITIES = [
  <KingBedIcon fontSize="small" />,
  <AcUnitIcon fontSize="small" />,
  <WifiIcon fontSize="small" />,
  <LocalBarIcon fontSize="small" />,
  <BathtubIcon fontSize="small" />
];

// Function to count room statuses
const countRoomStatuses = (rooms: any[]) => {
  let dirty = 0;
  let clean = 0;
  let management = 0;
  let outOfOrder = 0;
  let maintenance = 0;
  let assigned = 0;
  let available = 0;

  rooms.forEach((room) => {
    const status = room.item_status || room.room_status || 'clean';
    
    switch(status) {
      case 'dirty':
        dirty++;
        break;
      case 'clean':
        clean++;
        break;
      case 'management':
        management++;
        break;
      case 'out_of_order':
        outOfOrder++;
        break;
      case 'maintenance':
        maintenance++;
        break;
      case 'assigned':
        assigned++;
        break;
      default:
        available++;
        break;
    }
  });

  return {
    dirty,
    clean,
    management,
    out_of_order: outOfOrder,
    maintenance,
    assigned,
    available,
    total: rooms.length
  };
};

const RoomStatusReport: React.FC = () => {
  const theme = useTheme();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusCounts, setStatusCounts] = useState({
    dirty: 0,
    clean: 0,
    management: 0,
    out_of_order: 0,
    maintenance: 0,
    assigned: 0,
    available: 0,
    total: 0
  });
  
  // Dialog states
  const [historyDialogOpen, setHistoryDialogOpen] = useState<boolean>(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [assignmentHistory, setAssignmentHistory] = useState<AssignmentHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  
  // History filter states
  const [historyFilters, setHistoryFilters] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    housekeeperId: 'all'
  });
  
  // Housekeeper list for filter
  const [housekeeperList, setHousekeeperList] = useState<Array<{id: string, name: string}>>([]);
  const [housekeepers, setHousekeepers] = useState<Record<string, string>>({});
  
  // Snackbar states
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Housekeepers/getRoomsByBranch`, formData, config);
      
      console.log('Rooms response:', response.data);

      if (response.data.status === "true" || response.data.status === true) {
        const mappedRooms = response.data.data.map((room: any) => ({
          ...room,
          item_status: determineRoomStatus(room)
        }));
        
        setRooms(mappedRooms);
        
        const counts = countRoomStatuses(mappedRooms);
        setStatusCounts(counts);
        
        applyFilter(mappedRooms, selectedStatus, searchTerm);
      } else {
        showNotification('Failed to fetch rooms', 'error');
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showNotification('Error fetching room data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch housekeepers for filter
  const fetchHousekeepers = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      const response = await axios.post(`${baseUrl()}Housekeepers/getHousekeepers`, formData, config);
      
      if (response.data.status) {
        const hkMap: Record<string, string> = {};
        const hkList: Array<{id: string, name: string}> = [];
        
        response.data.data.forEach((hk: any) => {
          const name = hk.fullName || `${hk.firstName} ${hk.lastName}`.trim() || hk.email || 'Unknown';
          hkMap[hk.user_id] = name;
          hkList.push({ id: hk.user_id, name });
        });
        
        setHousekeepers(hkMap);
        setHousekeeperList(hkList.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error("Error fetching housekeepers:", error);
    }
  };

  // Fetch assignment history for a specific room
  const fetchAssignmentHistory = async (roomId: string) => {
    setHistoryLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("roomId", roomId);
      
      if (historyFilters.startDate) {
        formData.append("startDate", historyFilters.startDate.toISOString().split('T')[0]);
      }
      if (historyFilters.endDate) {
        formData.append("endDate", historyFilters.endDate.toISOString().split('T')[0]);
      }
      if (historyFilters.housekeeperId !== 'all') {
        formData.append("housekeeperId", historyFilters.housekeeperId);
      }

      const response = await axios.post(`${baseUrl()}Housekeepers/getRoomAssignmentHistory`, formData, config);
      
      console.log('Assignment history response:', response.data);

      if (response.data.status) {
        const historyData = response.data.data?.assignments || [];
        console.log('Assignment history data:', historyData);
        // // Enrich with names
        // const enriched = historyData.map((assignment: AssignmentHistory) => ({
        //   ...assignment,
        //   room_name: rooms.find(r => r.product_id === assignment.roomId)?.name || assignment.roomId,
        //   housekeeper_name: housekeepers[assignment.housekeeperId] || 'Unknown',
        //   supervisor_name: housekeepers[assignment.supervisorId] || 'Unknown'
        // }));
        
        setAssignmentHistory(historyData);
      } else {
        showNotification('Failed to fetch assignment history', 'error');
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showNotification('Error fetching assignment history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const determineRoomStatus = (room: any): string => {
    if (room.item_status) {
      return room.item_status;
    }
    
    if (room.is_assigned === true) {
      return 'assigned';
    }
    
    if (room.qty === 0) {
      return 'out_of_order';
    }
    
    if (room.totalQtyPosted && room.totalQtyPosted > 500) {
      return 'dirty';
    }
    
    if (room.totalQtyPosted && room.totalQtyPosted > 200) {
      return 'maintenance';
    }
    
    if (room.name && room.name.includes('VIP')) {
      return 'management';
    }
    
    if (Math.random() > 0.7) {
      return 'clean';
    }
    
    return 'available';
  };

  const applyFilter = (roomsData: Room[], status: string, search: string) => {
    let filtered = [...roomsData];

    if (status !== 'all') {
      filtered = filtered.filter(room => 
        (room.item_status || room.room_status) === status
      );
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      filtered = filtered.filter(room => 
        room.name.toLowerCase().includes(term) ||
        room.product_id.toLowerCase().includes(term) ||
        (room.assigned_housekeeper_name && room.assigned_housekeeper_name.toLowerCase().includes(term))
      );
    }

    setFilteredRooms(filtered);
  };

  useEffect(() => {
    fetchRooms();
    fetchHousekeepers();
  }, []);

  useEffect(() => {
    applyFilter(rooms, selectedStatus, searchTerm);
  }, [selectedStatus, searchTerm, rooms]);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleStatusClick = (status: string) => {
    setSelectedStatus(status);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleRefresh = () => {
    fetchRooms();
  };

  // Handle opening history dialog
  const handleViewHistory = (room: Room) => {
    setSelectedRoom(room);
    setHistoryDialogOpen(true);
    setHistoryFilters({
      startDate: null,
      endDate: null,
      housekeeperId: 'all'
    });
    fetchAssignmentHistory(room.product_id);
  };

  // Handle closing history dialog
  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedRoom(null);
    setAssignmentHistory([]);
  };

  // Handle history filter changes
  const handleHistoryFilterChange = (field: string, value: any) => {
    setHistoryFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply history filters
  const applyHistoryFilters = () => {
    if (selectedRoom) {
      fetchAssignmentHistory(selectedRoom.product_id);
    }
  };

  // Clear history filters
  const clearHistoryFilters = () => {
    setHistoryFilters({
      startDate: null,
      endDate: null,
      housekeeperId: 'all'
    });
    if (selectedRoom) {
      // Re-fetch without filters
      fetchAssignmentHistory(selectedRoom.product_id);
    }
  };

  const extractRoomNumber = (roomName: string): string => {
    const match = roomName.match(/ROOM\s+(\d+)/i);
    return match ? match[1] : roomName;
  };

  const getStatusStyle = (status: string = 'available') => {
    const option = STATUS_OPTIONS.find(opt => opt.value === status);
    return {
      bgcolor: option?.lightColor || '#f5f5f5',
      color: option?.color || '#757575',
      gradient: option?.gradient || 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
      icon: option?.icon || <HotelIcon />
    };
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned': return '#f59e0b';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'assigned': return 'Assigned';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const exportToCSV = () => {
    try {
      const headers = ['Room', 'Status', 'Assigned To', 'Price', 'Last Updated'];
      const csvData = filteredRooms.map(item => [
        item.name,
        item.item_status || 'unknown',
        item.assigned_housekeeper_name || 'Not Assigned',
        item.price,
        formatDate(item.last_updated)
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `room_status_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Report exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Failed to export report', 'error');
    }
  };

  const updatedStatusOptions = STATUS_OPTIONS.map(option => ({
    ...option,
    count: option.value === 'all' ? statusCounts.total : 
           option.value === 'clean' ? statusCounts.clean :
           option.value === 'dirty' ? statusCounts.dirty :
           option.value === 'management' ? statusCounts.management :
           option.value === 'out_of_order' ? statusCounts.out_of_order :
           option.value === 'maintenance' ? statusCounts.maintenance : 0
  }));

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ 
            backgroundColor: '#f8fafc', 
            minHeight: '100%', 
            display: 'flex', 
            flexDirection: 'column',
          }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                bgcolor: 'white',
                borderBottom: '1px solid #e2e8f0',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                borderRadius: 0
              }}
            >
              <Container maxWidth="xl">
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  justifyContent="space-between" 
                  alignItems={{ xs: 'start', sm: 'center' }}
                  spacing={2}
                  sx={{ py: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar 
                      sx={{ 
                        bgcolor: '#3b82f6',
                        color: 'white',
                        width: 48,
                        height: 48
                      }}
                    >
                      <HotelIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={600} color="#1e293b">
                        Room Status Dashboard
                      </Typography>
                      <Typography variant="body2" color="#64748b">
                        Monitor room conditions and cleaning history
                      </Typography>
                    </Box>
                  </Stack>
                  
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Export">
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={filteredRooms.length === 0}
                        size="small"
                        sx={{ 
                          borderColor: '#cbd5e1',
                          color: '#475569',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            bgcolor: '#eff6ff'
                          }
                        }}
                      >
                        Export
                      </Button>
                    </Tooltip>
                    <Tooltip title="Refresh">
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        size="small"
                        sx={{ 
                          bgcolor: '#3b82f6',
                          '&:hover': {
                            bgcolor: '#2563eb'
                          }
                        }}
                      >
                        Refresh
                      </Button>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Search Bar */}
              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <FilterIcon sx={{ color: '#3b82f6' }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#1e293b" sx={{ minWidth: 60 }}>
                    Filters
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by room name, number, or housekeeper..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#94a3b8' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSearch}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 2,
                        bgcolor: '#f8fafc',
                        '&:hover': {
                          bgcolor: '#f1f5f9'
                        }
                      }
                    }}
                  />
                </Stack>
              </Paper>

              {/* Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white'
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                          Total Rooms
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {statusCounts.total}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 48, height: 48 }}>
                        <HotelIcon />
                      </Avatar>
                    </Stack>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white'
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                          Clean Rooms
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {statusCounts.clean}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 48, height: 48 }}>
                        <CheckCircleOutlineIcon />
                      </Avatar>
                    </Stack>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white'
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                          Dirty Rooms
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {statusCounts.dirty}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 48, height: 48 }}>
                        <DeleteSweepIcon />
                      </Avatar>
                    </Stack>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white'
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                          Out of Order
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {statusCounts.out_of_order}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 48, height: 48 }}>
                        <BlockIcon />
                      </Avatar>
                    </Stack>
                  </Card>
                </Grid>
              </Grid>

              {/* Status Filter Chips */}
              <Stack 
                direction="row" 
                spacing={1} 
                sx={{ 
                  mb: 3, 
                  flexWrap: 'wrap', 
                  gap: 1,
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}
              >
                {updatedStatusOptions.map((status) => (
                  <Chip
                    key={status.value}
                    icon={React.cloneElement(status.icon, { 
                      sx: { color: selectedStatus === status.value ? 'white' : status.color }
                    })}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {status.label}
                        <Badge
                          badgeContent={status.count}
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              bgcolor: selectedStatus === status.value ? 'white' : status.color,
                              color: selectedStatus === status.value ? status.color : 'white',
                              fontSize: '0.7rem',
                              height: 18,
                              minWidth: 18
                            }
                          }}
                        />
                      </Box>
                    }
                    onClick={() => handleStatusClick(status.value)}
                    sx={{
                      px: 1,
                      py: 2,
                      borderRadius: 2,
                      bgcolor: selectedStatus === status.value ? status.color : 'white',
                      color: selectedStatus === status.value ? 'white' : status.color,
                      border: selectedStatus === status.value ? 'none' : `1px solid ${status.color}`,
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: selectedStatus === status.value ? status.color : status.lightColor,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 6px -1px ${alpha(status.color, 0.2)}`
                      },
                      '& .MuiChip-icon': {
                        color: selectedStatus === status.value ? 'white' : status.color
                      }
                    }}
                  />
                ))}
              </Stack>

              {/* Rooms Grid */}
              {loading ? (
                <Box sx={{ width: '100%', py: 8 }}>
                  <LinearProgress sx={{ borderRadius: 2 }} />
                  <Typography align="center" sx={{ mt: 2 }} color="#64748b">
                    Loading rooms...
                  </Typography>
                </Box>
              ) : filteredRooms.length === 0 ? (
                <Paper sx={{ 
                  p: 8, 
                  textAlign: 'center', 
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}>
                  <HotelIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" color="#475569" gutterBottom>
                    No rooms found
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Try adjusting your filters or refresh the page
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {filteredRooms.map((room, index) => {
                    const statusStyle = getStatusStyle(room.item_status || 'available');
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={room.id || room.product_id}>
                        <Fade in={true} timeout={300 + index * 50}>
                          <Card sx={{ 
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            transition: 'all 0.3s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 20px 25px -5px ${alpha(statusStyle.color, 0.2)}`
                            }
                          }}>
                            {/* Colorful Header based on status */}
                            <Box
                              sx={{
                                background: statusStyle.gradient,
                                p: 2,
                                color: 'white',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              {/* Decorative pattern */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -10,
                                  right: -10,
                                  width: 80,
                                  height: 80,
                                  borderRadius: '50%',
                                  background: 'rgba(255,255,255,0.1)',
                                  zIndex: 0
                                }}
                              />
                              
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                                <Box>
                                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                                    {extractRoomNumber(room.name)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    {room.name}
                                  </Typography>
                                </Box>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    width: 40,
                                    height: 40
                                  }}
                                >
                                  {React.cloneElement(statusStyle.icon, { sx: { fontSize: 20 } })}
                                </Avatar>
                              </Stack>
                            </Box>

                            <Box sx={{ p: 2.5 }}>
                              {/* Room Amenities */}
                              <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
                                {ROOM_AMENITIES.slice(0, 4).map((icon, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      p: 0.5,
                                      borderRadius: 1,
                                      bgcolor: '#f8fafc',
                                      color: statusStyle.color,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: `1px solid ${alpha(statusStyle.color, 0.2)}`
                                    }}
                                  >
                                    {icon}
                                  </Box>
                                ))}
                              </Stack>

                              {/* Status Badge */}
                              <Chip
                                label={(room.item_status || 'available').replace('_', ' ').toUpperCase()}
                                size="small"
                                sx={{
                                  bgcolor: alpha(statusStyle.color, 0.1),
                                  color: statusStyle.color,
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  mb: 2,
                                  borderRadius: 1,
                                  border: `1px solid ${alpha(statusStyle.color, 0.3)}`
                                }}
                              />

                              {/* Assignment Info */}
                              <Box sx={{ 
                                p: 1.5, 
                                bgcolor: '#f8fafc', 
                                borderRadius: 1.5,
                                border: '1px solid #e2e8f0'
                              }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                  <PersonIcon sx={{ fontSize: 14, color: statusStyle.color }} />
                                  <Typography variant="caption" color="#64748b">
                                    Assigned to
                                  </Typography>
                                </Stack>
                                <Typography variant="body2" fontWeight={600} color="#1e293b" sx={{ mb: 1.5 }}>
                                  {room.assigned_housekeeper_name || (
                                    <Box component="span" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                      Not Assigned
                                    </Box>
                                  )}
                                </Typography>
                                
                                <Divider sx={{ my: 1, borderColor: '#e2e8f0' }} />
                                
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    <Typography variant="caption" color="#64748b" display="block">
                                      Price / Night
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color={statusStyle.color}>
                                      ₦{parseInt(room.price).toLocaleString()}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" color="#64748b" display="block">
                                      Updated
                                    </Typography>
                                    <Typography variant="caption" fontWeight={500} color="#475569">
                                      {formatDate(room.last_updated)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>

                              {/* History Button - Click to view cleaning history */}
                              <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                startIcon={<HistoryIcon />}
                                onClick={() => handleViewHistory(room)}
                                sx={{
                                  mt: 2,
                                  borderColor: statusStyle.color,
                                  color: statusStyle.color,
                                  '&:hover': {
                                    borderColor: statusStyle.color,
                                    bgcolor: alpha(statusStyle.color, 0.1)
                                  },
                                  textTransform: 'none',
                                  borderRadius: 1.5
                                }}
                              >
                                View Cleaning History
                              </Button>
                            </Box>
                          </Card>
                        </Fade>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Container>

            {/* Cleaning History Dialog */}
            <Dialog
              open={historyDialogOpen}
              onClose={handleCloseHistoryDialog}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  maxHeight: '90vh'
                }
              }}
            >
              <DialogTitle sx={{ 
                p: 3, 
                borderBottom: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white'
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                      <HistoryIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Cleaning History
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {selectedRoom?.name} - Room {extractRoomNumber(selectedRoom?.name || '')}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton onClick={handleCloseHistoryDialog} sx={{ color: 'white' }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </DialogTitle>

              <DialogContent sx={{ p: 3 }}>
                {/* History Filters */}
                <Paper sx={{ 
                  p: 2, 
                  mb: 3, 
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#f8fafc'
                }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>
                    Filter History
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Start Date"
                        value={historyFilters.startDate}
                        onChange={(date) => handleHistoryFilterChange('startDate', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DateRangeIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="End Date"
                        value={historyFilters.endDate}
                        onChange={(date) => handleHistoryFilterChange('endDate', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DateRangeIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Housekeeper</InputLabel>
                        <Select
                          value={historyFilters.housekeeperId}
                          label="Housekeeper"
                          onChange={(e: SelectChangeEvent) => handleHistoryFilterChange('housekeeperId', e.target.value)}
                        >
                          <MenuItem value="all">All Housekeepers</MenuItem>
                          {housekeeperList.map(hk => (
                            <MenuItem key={hk.id} value={hk.id}>
                              {hk.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={clearHistoryFilters}
                      startIcon={<ClearIcon />}
                    >
                      Clear
                    </Button>
                    <Button 
                      variant="contained" 
                      size="small" 
                      onClick={applyHistoryFilters}
                      startIcon={<SearchIcon />}
                      sx={{ bgcolor: '#3b82f6' }}
                    >
                      Apply Filters
                    </Button>
                  </Stack>
                </Paper>

                {/* History Table */}
                {historyLoading ? (
                  <Box sx={{ py: 4 }}>
                    <LinearProgress />
                    <Typography align="center" sx={{ mt: 2 }} color="#64748b">
                      Loading history...
                    </Typography>
                  </Box>
                ) : assignmentHistory.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <AssignmentIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                    <Typography color="#475569" gutterBottom>
                      No cleaning history found
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      Try adjusting your filters or check back later
                    </Typography>
                  </Paper>
                ) : (
                  <TableContainer component={Paper} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Table>
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Housekeeper</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Supervisor</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Assignment Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignmentHistory.map((history) => (
                          <TableRow key={history.id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {formatShortDate(history.createdAt)}
                              </Typography>
                              <Typography variant="caption" color="#64748b">
                                {new Date(history.createdAt).toLocaleTimeString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{ width: 24, height: 24, bgcolor: alpha('#f59e0b', 0.1) }}>
                                  <PersonIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                                </Avatar>
                                <Typography variant="body2">
                                  {history.housekeeper_name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {history.supervisor_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusLabel(history.status)}
                                size="small"
                                sx={{
                                  bgcolor: alpha(getStatusColor(history.status), 0.1),
                                  color: getStatusColor(history.status),
                                  fontWeight: 600,
                                  fontSize: '0.7rem'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatShortDate(history.assignmentDate)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
                <Button 
                  onClick={handleCloseHistoryDialog}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* Notifications */}
            <Snackbar 
              open={snackbar.open} 
              autoHideDuration={6000} 
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert 
                onClose={handleCloseSnackbar} 
                severity={snackbar.severity} 
                sx={{ 
                  fontWeight: 500,
                  borderRadius: 2,
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
                variant="filled"
              >
                {snackbar.message}
              </Alert>
            </Snackbar>

            {/* Loading Overlay */}
            <Backdrop 
              sx={{ 
                color: '#3b82f6', 
                zIndex: theme.zIndex.drawer + 1,
                backdropFilter: 'blur(3px)',
                bgcolor: 'rgba(255, 255, 255, 0.8)'
              }} 
              open={loading && filteredRooms.length === 0}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress color="inherit" />
                <Typography variant="body2" sx={{ color: '#1e293b' }}>
                  Loading rooms...
                </Typography>
              </Stack>
            </Backdrop>

            {/* Add pulse animation */}
            <style>
              {`
                @keyframes pulse {
                  0% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.5; transform: scale(1.1); }
                  100% { opacity: 1; transform: scale(1); }
                }
              `}
            </style>
          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default RoomStatusReport;